import { AudioDeviceSetupModal } from "@/components/AudioDeviceSetupModal";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useDictation } from "@/hooks/useDictation";
import { cn } from "@/utils";
import * as analytics from "@/utils/analytics";
import { AudioDevice, audioSettingsStorage } from "@/utils/audioSettings";
import { Spinner } from "@cohesive/ui-components";
import createDebug from "debug";
import { ArrowUp, Loader2, Mic, MicOff, Square } from "lucide-react";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import toast from "react-hot-toast";
import { AudioWaveform } from "./AudioWaveform";

const debug = createDebug("cohesive:audio-recording");

interface AudioRecordingButtonProps {
  onTranscriptionComplete?: (text: string) => void;
  /** Streams the full running transcript while the user speaks so the host
   * can render it live (MessageInput writes it into the editor). */
  onInterimTranscript?: (text: string) => void;
  /** Fired when the user cancels dictation (Escape) so the host can restore
   * its pre-dictation state. */
  onDictationCancel?: () => void;
  /** When provided, a send button appears during recording. Clicking it stops
   * recording and passes the transcribed text directly to this callback,
   * bypassing the editor. */
  onSendImmediately?: (text: string) => void;
  disabled?: boolean;
  /** Mic / stop / error states — same as `MessageInput` `audioRecordingButton`. */
  className?: string;
  /** Send-while-recording control — same classes as `SendButton` `sendButtonClassName`. */
  sendButtonClassName?: string;
  /** Arrow icon on send-while-recording — same as `SendButton` `iconClassName`. */
  sendIconClassName?: string;
  micIconClassName?: string;
  /** Tracks the whole dictation session — true from recording start until the
   * final text settles (complete, cancel, or error). Spans `isFinalizing`, so
   * the host keeps the editor locked while the final transcript is still
   * going to replace its contents. */
  onDictationActiveChange?: (active: boolean) => void;
}

export interface AudioRecordingButtonRef {
  startRecording: () => Promise<void>;
  stopRecording: () => void;
}

export const AudioRecordingButton = forwardRef<
  AudioRecordingButtonRef,
  AudioRecordingButtonProps
>(
  (
    {
      onTranscriptionComplete,
      onInterimTranscript,
      onDictationCancel,
      onSendImmediately,
      disabled = false,
      className,
      sendButtonClassName,
      sendIconClassName,
      micIconClassName,
      onDictationActiveChange,
    },
    ref,
  ) => {
    const [showDeviceSetup, setShowDeviceSetup] = useState(false);

    // When true, the next transcription result is sent immediately via onSendImmediately
    const sendImmediatelyRef = useRef(false);
    const onSendImmediatelyRef = useRef(onSendImmediately);
    onSendImmediatelyRef.current = onSendImmediately;
    const onTranscriptionCompleteRef = useRef(onTranscriptionComplete);
    onTranscriptionCompleteRef.current = onTranscriptionComplete;

    const handleFinalText = useCallback((text: string) => {
      debug("final transcript", { length: text.length });
      if (!text.trim()) {
        sendImmediatelyRef.current = false;
        toast.error("No transcription text received");
        return;
      }
      if (sendImmediatelyRef.current && onSendImmediatelyRef.current) {
        sendImmediatelyRef.current = false;
        onSendImmediatelyRef.current(text);
      } else {
        sendImmediatelyRef.current = false;
        onTranscriptionCompleteRef.current?.(text);
      }
    }, []);

    const handleError = useCallback((error: Error) => {
      sendImmediatelyRef.current = false;
      debug("dictation error:", error);
      toast.error(error.message);
    }, []);

    const maxDurationSecondsRef = useRef(120);

    const {
      isRecording,
      isSupported,
      audioLevel,
      error,
      duration,
      interimText,
      isFinalizing,
      maxDurationSeconds,
      startRecording,
      stopRecording,
      cancelRecording,
    } = useDictation({
      onTranscriptionComplete: handleFinalText,
      onError: handleError,
      onMaxDurationReached: () => {
        analytics.trackAudioRecordingStopped({
          durationSeconds: maxDurationSecondsRef.current,
          wasAutoStopped: true,
        });
        toast(
          `Recording stopped automatically after reaching the ${Math.round(
            maxDurationSecondsRef.current / 60,
          )}-minute limit. Transcribing audio...`,
          { icon: "⏱️" },
        );
      },
    });

    maxDurationSecondsRef.current = maxDurationSeconds;

    const countdownFrom = maxDurationSeconds - 30;

    const onInterimTranscriptRef = useRef(onInterimTranscript);
    onInterimTranscriptRef.current = onInterimTranscript;
    useEffect(() => {
      if (interimText) {
        onInterimTranscriptRef.current?.(interimText);
      }
    }, [interimText]);

    const handleSendWhileRecording = useCallback(() => {
      sendImmediatelyRef.current = true;
      if (isRecording) {
        analytics.trackAudioRecordingStopped({
          durationSeconds: duration,
          wasAutoStopped: false,
        });
        stopRecording();
      }
    }, [isRecording, stopRecording, duration]);

    const handleToggleRecording = useCallback(async () => {
      if (isRecording) {
        analytics.trackAudioRecordingStopped({
          durationSeconds: duration,
          wasAutoStopped: false,
        });
        stopRecording();
      } else {
        // Check if a device is configured before starting recording
        if (!audioSettingsStorage.hasConfiguredDevice()) {
          setShowDeviceSetup(true);
          return;
        }
        analytics.trackAudioRecordingStarted();
        await startRecording();
      }
    }, [isRecording, startRecording, stopRecording, duration]);

    const handleDeviceSelected = useCallback(
      (device: AudioDevice) => {
        toast.success(`Microphone "${device.label}" configured successfully!`);
        setShowDeviceSetup(false);

        // Automatically start recording after device is configured
        setTimeout(() => {
          analytics.trackAudioRecordingStarted();
          void startRecording();
        }, 100);
      },
      [startRecording],
    );

    const handleDeviceSetupCancel = useCallback(() => {
      setShowDeviceSetup(false);
    }, []);

    // Expose startRecording and stopRecording functions through ref
    useImperativeHandle(
      ref,
      () => ({
        startRecording: async () => {
          if (!audioSettingsStorage.hasConfiguredDevice()) {
            setShowDeviceSetup(true);
            return;
          }
          analytics.trackAudioRecordingStarted();
          await startRecording();
        },
        stopRecording: () => {
          if (isRecording) {
            analytics.trackAudioRecordingStopped({
              durationSeconds: duration,
              wasAutoStopped: false,
            });
          }
          stopRecording();
        },
      }),
      [startRecording, stopRecording, isRecording, duration],
    );

    // Notify the host while dictation owns the composer — through finalizing,
    // not just recording, since the final transcript still replaces the field.
    const dictationActive = isRecording || isFinalizing;
    useEffect(() => {
      onDictationActiveChange?.(dictationActive);
    }, [dictationActive, onDictationActiveChange]);

    // Capture-phase shortcuts while recording (match canvas chat input)
    useEffect(() => {
      if (!isRecording) return;
      const handler = (e: KeyboardEvent) => {
        if (e.key === " " || e.key === "Enter") {
          e.preventDefault();
          e.stopPropagation();
          analytics.trackAudioRecordingStopped({
            durationSeconds: duration,
            wasAutoStopped: false,
          });
          stopRecording();
        }
        if (e.key === "Escape") {
          e.preventDefault();
          e.stopPropagation();
          analytics.trackAudioRecordingStopped({
            durationSeconds: duration,
            wasAutoStopped: false,
          });
          cancelRecording();
          onDictationCancel?.();
        }
      };
      document.addEventListener("keydown", handler, true);
      return () => document.removeEventListener("keydown", handler, true);
    }, [
      isRecording,
      stopRecording,
      cancelRecording,
      onDictationCancel,
      duration,
    ]);

    if (!isSupported) {
      return (
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              className={cn(
                "w-8 h-8 rounded-full text-neutral-400 cursor-not-allowed",
                className,
              )}
              disabled
            >
              <MicOff className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            Audio recording not supported in this browser
          </TooltipContent>
        </Tooltip>
      );
    }

    if (error) {
      return (
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              className={cn(
                "w-8 h-8 rounded-full text-lc-blocked hover:text-lc-blocked dark:hover:text-lc-blocked transition-colors",
                className,
              )}
              onClick={handleToggleRecording}
              disabled={disabled}
            >
              <MicOff className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            Recording error: {error}
          </TooltipContent>
        </Tooltip>
      );
    }

    return (
      <>
        <div className="flex items-center">
          {/* Recording / finalizing row (shared layout; send-while-recording only if onSendImmediately) */}
          {(isRecording || isFinalizing) &&
          (onSendImmediately || onTranscriptionComplete) ? (
            <>
              {/* Left: stop button — always enabled so users can transcribe even
                  while the session is processing */}
              {isRecording && (
                <Button
                  variant="ghost"
                  className={cn(
                    "w-8 h-8 rounded-full py-1 px-1 transition-colors cursor-pointer hover:bg-transparent",
                    "text-lc-blocked hover:text-lc-blocked dark:hover:text-lc-blocked",
                    className,
                  )}
                  onClick={handleToggleRecording}
                >
                  <Square className="h-3 w-3 fill-current" />
                </Button>
              )}

              {/* Center: timer + live caption + waveform while recording; spinner while finalizing */}
              <div className="flex items-center gap-1.5 mx-1 mr-3 flex-1 min-w-0 justify-center">
                {isRecording ? (
                  <>
                    <span
                      className={cn(
                        "text-xs font-mono tabular-nums transition-colors shrink-0",
                        duration >= countdownFrom
                          ? "text-lc-blocked dark:text-lc-blocked font-semibold"
                          : "text-neutral-500 dark:text-neutral-400",
                      )}
                    >
                      {String(Math.floor(duration / 60)).padStart(2, "0")}:
                      {String(duration % 60).padStart(2, "0")}
                      {duration >= countdownFrom && (
                        <span className="ml-1 text-[10px]">
                          ({maxDurationSeconds - duration}s left)
                        </span>
                      )}
                    </span>
                    <AudioWaveform
                      audioLevel={audioLevel}
                      isRecording={isRecording}
                    />
                  </>
                ) : (
                  <div className="flex items-center gap-2 text-sm text-foreground">
                    <Spinner size="sm" />
                  </div>
                )}
              </div>

              {/* Right: send button — only when immediate send is supported */}
              {isRecording && onSendImmediately && (
                <Button
                  onClick={handleSendWhileRecording}
                  variant={disabled ? "outline" : "ghost"}
                  className={cn(
                    "rounded-full w-9 h-9 transition-all duration-200 disabled:opacity-100",
                    disabled
                      ? "cursor-not-allowed border-input bg-transparent text-muted-foreground hover:bg-transparent dark:hover:bg-transparent"
                      : "bg-primary hover:bg-primary/90 text-primary-foreground border-transparent hover:border-transparent",
                    sendButtonClassName,
                  )}
                  disabled={disabled}
                  size="icon"
                >
                  <ArrowUp
                    className={cn(
                      "h-5! w-5!",
                      sendIconClassName,
                      "transition-colors",
                      disabled
                        ? "text-muted-foreground"
                        : "text-primary-foreground",
                    )}
                  />
                </Button>
              )}
            </>
          ) : (
            <>
              {/* Legacy layout: waveform+timer left, mic/stop button right */}
              {(isRecording || isFinalizing) && (
                <div className="flex items-center space-x-2 mr-2">
                  {isFinalizing ? (
                    <div className="flex items-center gap-2 text-sm text-foreground">
                      <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground shrink-0" />
                      <span>Transcribing…</span>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-1">
                      <AudioWaveform
                        audioLevel={audioLevel}
                        isRecording={isRecording}
                      />
                      <span
                        className={cn(
                          "text-xs font-mono tabular-nums transition-colors",
                          duration >= countdownFrom
                            ? "text-lc-blocked dark:text-lc-blocked font-semibold"
                            : "text-neutral-500 dark:text-neutral-400",
                        )}
                      >
                        {String(Math.floor(duration / 60)).padStart(2, "0")}:
                        {String(duration % 60).padStart(2, "0")}
                        {duration >= countdownFrom && (
                          <span className="ml-1 text-[10px]">
                            ({maxDurationSeconds - duration}s left)
                          </span>
                        )}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Recording button */}
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    className={cn(
                      "w-8 h-8 rounded-full py-1 px-1 transition-colors cursor-pointer hover:bg-accent",
                      isRecording
                        ? "text-lc-blocked hover:text-lc-blocked dark:hover:text-lc-blocked"
                        : // Theme-aware hover (matches every other toolbar
                          // button) — `hover:text-white` turned the mic invisible
                          // on the light-mode input.
                          "text-muted-foreground hover:text-foreground",
                      className,
                    )}
                    onClick={handleToggleRecording}
                    disabled={disabled || isFinalizing}
                  >
                    {isRecording ? (
                      <Square className="h-3 w-3 fill-current" />
                    ) : (
                      <Mic className={cn("h-5! w-5!", micIconClassName)} />
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">Dictate</TooltipContent>
              </Tooltip>
            </>
          )}
        </div>

        {/* Audio Device Setup Modal */}
        <AudioDeviceSetupModal
          isOpen={showDeviceSetup}
          onClose={handleDeviceSetupCancel}
          onDeviceSelected={handleDeviceSelected}
          title="Set up your microphone"
          description="To start recording, please select and test your microphone. This setup only needs to be done once."
        />
      </>
    );
  },
);

AudioRecordingButton.displayName = "AudioRecordingButton";
